import { nextTick, reactive } from 'vue';

import { TeleportSymbol } from './components/CdsOverlay/composable';
import { CssGridSymbol } from './components/CdsGrid/composable';
import { createDisplay, createTheme, DisplaySymbol, LocaleSymbol, ThemeSymbol } from './composable';
import { getUid, IN_BROWSER } from './utils';

import type { ILocale, TAvailableLocale } from './locale';
import type { IDisplayOptions, IThemeOptions } from './composable';
import type { ITeleportOptions } from './components/CdsOverlay/composable';
import type { App, ComponentPublicInstance, InjectionKey, Component, Directive, ComponentInternalInstance } from 'vue';

export interface ICdsOptions {
  components?: Record<string, Component>;
  directives?: Record<string, Directive>;
  icons?: Record<string, Component>;
  illustrations?: Record<string, Component>;
  display?: IDisplayOptions;
  theme?: IThemeOptions;
  cssGrid?: boolean;
  ssr?: boolean;
  locale?: TAvailableLocale | ILocale;
  teleport?: Partial<ITeleportOptions>;
}

const defaultOptions: ICdsOptions = {
  cssGrid: false,
  ssr: false,
  locale: 'ru'
};

interface NuxtApp {
  $nuxt?: { hook: (name: string, fn: () => void) => void };
}

export function createCds(options: ICdsOptions = {}) {
  options = { ...defaultOptions, ...options };
  const { components = {}, directives = {}, icons = {}, illustrations = {} } = options;

  const display = createDisplay(options.display, options.ssr);
  const theme = createTheme(options.theme);

  const install = (app: App & NuxtApp) => {
    Object.entries({ ...components }).forEach(([name, component]) => {
      app.component(name, component as Component);
    });

    Object.entries({ ...directives }).forEach(([name, directive]) => {
      app.directive(name, directive as Directive);
    });

    Object.entries({ ...icons }).forEach(([name, icon]) => {
      app.component(name, icon as Component);
    });

    Object.entries({ ...illustrations }).forEach(([name, illustration]) => {
      app.component(name, illustration as Component);
    });

    theme.install(app);

    app.provide(DisplaySymbol, display);
    app.provide(ThemeSymbol, theme);
    app.provide(CssGridSymbol, !!options.cssGrid);
    app.provide(LocaleSymbol, options.locale as TAvailableLocale | ILocale);
    app.provide(TeleportSymbol, options.teleport as ITeleportOptions);

    if (IN_BROWSER && options.ssr) {
      if (app.$nuxt) {
        app.$nuxt.hook('app:suspense:resolve', () => {
          display.update();
        });
      } else {
        const { mount } = app;
        app.mount = (...args) => {
          const vm = mount(...args);
          nextTick(() => display.update());
          app.mount = mount;
          return vm;
        };
      }
    }

    getUid.reset();

    if (typeof __VUE_OPTIONS_API__ !== 'boolean' || __VUE_OPTIONS_API__) {
      app.mixin({
        computed: {
          $cds() {
            return reactive({
              display: inject.call(this, DisplaySymbol)
            });
          }
        }
      });
    }
  };

  return {
    install,
    display,
    theme
  };
}

function inject(this: ComponentPublicInstance, key: InjectionKey<any> | string) {
  const vm = this.$;

  const provides =
    (vm.parent as ComponentInternalInstance & { provides: Record<string, unknown> | undefined })?.provides ??
    vm.vnode.appContext?.provides;

  if (provides && (key as any) in provides) {
    return provides[key as string];
  }
}
